import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Globe, Menu, X, LayoutDashboard, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';

const Logo = () => (
  <div className="flex items-center gap-3">
    <div className="w-12 h-12 relative flex items-center justify-center">
      <div className="absolute inset-0 rotate-45 border-2 border-white/90 rounded-sm" />
      <div className="absolute inset-1 rotate-45 border border-white/50 rounded-sm" />
      <span className="relative text-white font-serif text-lg font-semibold tracking-wide">PL</span>
    </div>
    <div className="leading-tight">
      <div className="text-white font-semibold tracking-[0.18em] text-lg">PATAGONIA LOBBY</div>
      <div className="text-white/60 text-[10px] tracking-[0.25em] uppercase font-light">Business &amp; Managment</div>
    </div>
  </div>
);

const navLinks = [
  { id: 'servicios', label: 'Servicios' },
  { id: 'proceso', label: 'Proceso' },
  { id: 'sectores', label: 'Sectores' },
];

export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNavClick = (id) => {
    setOpen(false);
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: id } });
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? 'bg-[#062b20]/95 backdrop-blur-md' : 'bg-[#07301f]'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-[88px] flex items-center justify-between">
        <Link to="/" className="shrink-0">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {navLinks.map((link) => (
            <button
              key={link.id}
              onClick={() => handleNavClick(link.id)}
              className="text-white/85 hover:text-white text-[15px] font-medium tracking-wide transition-colors"
            >
              {link.label}
            </button>
          ))}
          <div className="flex items-center gap-1.5 text-white/85 text-[15px] font-medium">
            <Globe className="w-4 h-4" />
            <span>ES</span>
          </div>

          {/* Admin access */}
          <button
            onClick={() => navigate(user ? '/admin' : '/admin/login')}
            title={user ? 'Ir al panel de administración' : 'Acceso administración'}
            className="flex items-center gap-2 text-white/75 hover:text-white text-[13px] font-medium tracking-wide transition-colors border border-white/20 hover:border-white/50 rounded-md px-3 h-9"
          >
            {user ? <LayoutDashboard className="w-4 h-4" /> : <Lock className="w-3.5 h-3.5" />}
            {user ? 'Dashboard' : 'Admin'}
          </button>

          <Button
            onClick={() => navigate('/solicitar')}
            variant="outline"
            className="border-white/90 bg-transparent text-white hover:bg-white hover:text-[#07301f] rounded-md px-5 h-11 font-medium"
          >
            Solicitar Cotización
          </Button>
        </nav>

        <button
          className="lg:hidden text-white"
          onClick={() => setOpen(!open)}
          aria-label="menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#062b20] border-t border-white/10 px-6 py-6 flex flex-col gap-4">
          {navLinks.map((link) => (
            <button
              key={link.id}
              onClick={() => handleNavClick(link.id)}
              className="text-white/90 text-left text-base font-medium"
            >
              {link.label}
            </button>
          ))}
          <Button
            onClick={() => { setOpen(false); navigate('/solicitar'); }}
            variant="outline"
            className="border-white/90 bg-transparent text-white hover:bg-white hover:text-[#07301f] rounded-md h-11"
          >
            Solicitar Cotización
          </Button>
          <button
            onClick={() => { setOpen(false); navigate(user ? '/admin' : '/admin/login'); }}
            className="flex items-center justify-center gap-2 text-white/80 text-sm border border-white/20 rounded-md h-11"
          >
            {user ? <LayoutDashboard className="w-4 h-4" /> : <Lock className="w-3.5 h-3.5" />}
            {user ? 'Dashboard' : 'Acceso Administración'}
          </button>
        </div>
      )}
    </header>
  );
}
